export type SearchLocale = "en-US" | "ja-JP" | "ko-KR";

type StaticSearchCopyKey =
  | "all"
  | "back"
  | "clearRecent"
  | "closeSearch"
  | "comments"
  | "issues"
  | "loadMore"
  | "loading"
  | "loadingSuggestions"
  | "noResultsHint"
  | "people"
  | "posts"
  | "recent"
  | "recentIssues"
  | "recentSearches"
  | "removeRecent"
  | "searchError"
  | "searchPlaceholder"
  | "searchTitle"
  | "searches"
  | "seeAllResults"
  | "seeAll"
  | "tags"
  | "trending";

type SearchTypeLabelKey =
  | "comment"
  | "issue"
  | "post"
  | "tag"
  | "team"
  | "user";

const SEARCH_COPY: Record<
  SearchLocale,
  Record<StaticSearchCopyKey | SearchTypeLabelKey, string>
> = {
  "en-US": {
    all: "All",
    back: "Back",
    clearRecent: "Clear all",
    closeSearch: "Close search",
    comment: "Comment",
    comments: "Comments",
    issue: "Issue",
    issues: "Issues",
    loadMore: "Load more",
    loading: "Loading...",
    loadingSuggestions: "Loading suggestions...",
    noResultsHint: "Try broader keywords, different wording, or check the spelling.",
    people: "People",
    post: "Post",
    posts: "Posts",
    recent: "Recent",
    recentIssues: "Recent issues",
    recentSearches: "Recent searches",
    removeRecent: "Remove recent search",
    searchError: "Unable to load search results right now.",
    searchPlaceholder: "Find anything on Societea",
    searches: "Searches",
    searchTitle: "Search",
    seeAll: "See all",
    seeAllResults: "See all results",
    tag: "Tag",
    tags: "Tags",
    team: "Partea",
    trending: "Trending",
    user: "User",
  },
  "ja-JP": {
    all: "すべて",
    back: "戻る",
    clearRecent: "すべて消去",
    closeSearch: "検索を閉じる",
    comment: "コメント",
    comments: "コメント",
    issue: "課題",
    issues: "課題",
    loadMore: "もっと見る",
    loading: "読み込み中...",
    loadingSuggestions: "候補を読み込み中...",
    noResultsHint: "もう少し広いキーワードにするか、表記を変えるか、スペルをご確認ください。",
    people: "人",
    post: "投稿",
    posts: "投稿",
    recent: "最近",
    recentIssues: "最近の課題",
    recentSearches: "最近の検索",
    removeRecent: "最近の検索を削除",
    searchError: "現在検索結果を読み込めません。",
    searchPlaceholder: "課題、投稿、人、タグを検索...",
    searches: "検索",
    searchTitle: "検索",
    seeAll: "すべて見る",
    seeAllResults: "結果をすべて見る",
    tag: "タグ",
    tags: "タグ",
    team: "Partea",
    trending: "注目",
    user: "ユーザー",
  },
  "ko-KR": {
    all: "전체",
    back: "뒤로",
    clearRecent: "모두 지우기",
    closeSearch: "검색 닫기",
    comment: "댓글",
    comments: "댓글",
    issue: "이슈",
    issues: "이슈",
    loadMore: "더 보기",
    loading: "불러오는 중...",
    loadingSuggestions: "제안을 불러오는 중...",
    noResultsHint: "더 넓은 키워드를 쓰거나 표현을 바꾸거나 철자를 확인해 보세요.",
    people: "사람",
    post: "게시물",
    posts: "게시물",
    recent: "최근",
    recentIssues: "최근 이슈",
    recentSearches: "최근 검색",
    removeRecent: "최근 검색 삭제",
    searchError: "지금은 검색 결과를 불러올 수 없습니다.",
    searchPlaceholder: "이슈, 게시물, 사람, 태그 검색...",
    searches: "검색",
    searchTitle: "검색",
    seeAll: "전체 보기",
    seeAllResults: "전체 결과 보기",
    tag: "태그",
    tags: "태그",
    team: "Partea",
    trending: "인기",
    user: "사용자",
  },
};

export const getSearchCopy = (
  locale: SearchLocale,
  key: StaticSearchCopyKey | SearchTypeLabelKey
): string => SEARCH_COPY[locale][key];

export const getNoResultsText = (locale: SearchLocale, query: string): string => {
  switch (locale) {
    case "ja-JP":
      return `"${query}" の検索結果はありません`;
    case "ko-KR":
      return `"${query}"에 대한 결과가 없습니다`;
    case "en-US":
    default:
      return `No results for "${query}"`;
  }
};

export const getResultCountText = (locale: SearchLocale, count: number): string => {
  switch (locale) {
    case "ja-JP":
      return `${count} 件の結果`;
    case "ko-KR":
      return `${count}개 결과`;
    case "en-US":
    default:
      return `${count} result${count === 1 ? "" : "s"}`;
  }
};

export const getSeeAllResultsText = (locale: SearchLocale, query: string): string => {
  switch (locale) {
    case "ja-JP":
      return `"${query}" の結果をすべて見る`;
    case "ko-KR":
      return `"${query}"의 전체 결과 보기`;
    case "en-US":
    default:
      return `See all results for "${query}"`;
  }
};
